import { localAttackRolls, localRollInsert, presentLocalAbilityVfx } from './local-roll-presentation';
import { encounterCreatureField } from '../shared/array-types';
type Row = Record<string, unknown>;
const row = (value: unknown): Row => value && typeof value === 'object' && !Array.isArray(value) ? value as Row : {};
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(value => value && typeof value === 'object') as Row[] : [];
const effects = (command: Row): unknown[] => [command.verdict, command.gm_boundary, command.tick, command.hunger_tick,
    row(command.recovery).verdict, row(command.arm).verdict, row(command.check).verdict,
    command.key_boundary, row(command.trap_fire).verdict, row(command.fire).verdict, row(command.verdict).save, command.removal];

export function truthTeleportOffers(command: Row): Row[] {
    const offers = new Map<unknown, Row>();
    for (const offer of rows(command.teleport_offers)) offers.set(offer.id, offer);
    for (const part of [...effects(command), row(row(command.trap).fire).verdict])
        for (const offer of rows(row(part).teleportOffers)) offers.set(offer.id, offer);
    return [...offers.values()];
}

/** Presentation of the decided command, shared by the initiating page and socket peers.
 * These are the same insert_roll inputs used by the local sink; no rule is rerun. */
export function truthPresentationRows(command: Row): Row[] {
    if (Array.isArray(command.truth_presentation_rows)) return rows(command.truth_presentation_rows);
    const result: Row[] = [];
    for (const part of effects(command)) result.push(...rows(row(part).rows));
    result.push(...rows(command.rows), ...rows(row(command.plan).rows), ...rows(row(command.plan).logs),
        ...rows(row(command.time_save).rows), ...rows(row(command.activity).rows), ...rows(row(row(command.timed).activity).rows));
    const actor = command.actor_id;
    const log = (type: string, more: Row = {}) => result.push({ roll_type_1: type, roll_by: actor, roll_to: '', ...more });
    switch (command.schema) {
        case 'janus.movement.truth.v1': {
            const fire = row(row(command.trap).fire);
            if (fire.mode === 'rolled-v2') {
                const skipped = row(row(fire.verdict).lifecycle).skipped;
                for (const reason of Array.isArray(skipped) ? skipped : []) log('janus_unported_skip', { private: 1, rolls_full: reason });
            }
            for (const part of [command.status_plan, row(command.overrun).verdict, row(command.climb).verdict, row(row(command.trap).fire).verdict])
                result.push(...rows(row(part).rows));
            for (const contest of rows(row(command.stealth).contests)) result.push(...rows(contest.rows));
            for (const effect of rows(row(row(command.stealth).effects).rows)) result.push({
                roll_type_1: effect.kind === 'reveal' ? 'target_creature_revealed' : 'stealth_spotted_gm_prompt',
                roll_by: effect.observer, roll_to: effect.subject, roll_id: effect.roll_id,
                private: effect.kind === 'prompt' ? 1 : 0, voice: effect.voice });
            for (const reason of Array.isArray(command.dispatch_skips) ? command.dispatch_skips : [])
                log('janus_unported_skip', { private: 1, rolls_full: reason });
            break;
        }
        case 'janus.status.geometry.truth.v1':
            if (command.show_log_message === 1 && Number(command.ft_moved) > 0)
                log('status_effect_moved', { roll_item_id: command.status_id, roll_result: command.ft_moved });
            break;
        case 'janus.action.attack.v2': result.push(...localAttackRolls(command, row(command.frame))); break;
        case 'janus.inventory.equip.v1': {
            const types: Record<string, string> = { equip: 'equip_inventory', both_hands: 'equip_both_hands',
                unequip_both_hands: 'unequip_both_hands', swap_hands: 'swapped_hands' };
            if (types[String(command.kind)]) log(types[String(command.kind)]!, { roll_to: actor,
                roll_item_id: command.kind === 'swap_hands' ? '' : command.inventory_id });
            break;
        }
        case 'janus.time.push.v1':
            if (command.gm_log) log('gm_pushed_campaign_time', { roll_result: command.log_seconds ?? command.seconds });
            break;
        case 'janus.combat.v1': {
            const plan = row(command[String(command.action)]);
            for (const parts of [row(plan.begins).verdicts, row(plan.initiative).rolls, row(plan.contingencies).steps])
                for (const part of rows(parts)) result.push(...rows(row(part.verdict).rows));
            result.push(...rows(row(row(plan.first).tick).rows), ...rows(plan.first_removal_rows));
            if (command.action === 'start' && row(plan.first).id) log('start_turn', { roll_by: row(plan.first).id });
            else if (command.action === 'exit') log('combat_ended', { roll_to: command.encounter_id });
            else if (command.action === 'previous' || command.action === 'goto') log(command.action === 'goto' ? 'go_to_creature_turn' : 'previous_turn', { roll_to: command.action === 'goto' ? row(command.nav).next_id : '' });
            break;
        }
        case 'janus.turn.end.v1': {
            const logs = row(command.writes).log;
            if (Array.isArray(logs)) {
                log(String(logs[0])); log(String(logs[1]), { roll_by: command.next_id });
            }
            break;
        }
        case 'janus.resource.truth.v1': {
            const kind = String(command.kind), after = row(command.after);
            const types: Record<string, string> = { luck: 'self_adjust_luck', mana: 'self_adjust_mana', hunger: 'self_adjust_hunger',
                thirst: 'self_adjust_thirst', temp_v: 'self_adjust_temp_v', max_temp_v: 'self_adjust_temp_max_v',
                max_v: 'self_adjust_max_v', v: 'self_adjust_v', heal_to_max: 'heal_to_v_max', add_ap: 'add_ap', add_reaction: 'add_reaction' };
            if (kind === 'award_luck') for (const target of rows(command.targets)) log('gm_award_luck', { roll_to: target.actor_id, roll_result: 1 });
            else if (['weapon_attack', 'head_attack'].includes(kind)) log(`${Number(command.delta) > 0 ? 'increased' : 'decreased'}_${kind}`, { roll_result: after[kind] });
            else if (types[kind]) log(types[kind]!, { roll_result: command.delta });
            break;
        }
        case 'janus.action.truth.v1': {
            const key = String(row(row(command.writes).log).key ?? '');
            if (key) {
                log('no_roll', { roll_type_2: key, roll_item_id: command.ability_id, roll_to: actor });
                if (rows(row(command.writes).statuses_insert).length) log(`${key}_applied`, { roll_item_id: command.ability_id, roll_to: actor });
            }
            break;
        }
        case 'janus.status.truth.v1':
        case 'janus.status.truth.v2':
            if (command.kind === 'trigger_clear') log('triggers_cleared');
            else if (['status_pause', 'status_resume'].includes(String(command.kind))) log(command.kind === 'status_pause' ? 'status_effect_paused' : 'status_effect_resumed', { roll_item_id: command.ability_id });
            break;
    }
    for (const op of rows(command.ops)) {
        if (op.op === 'step') { result.push(...rows(op.rows)); continue; }
        if (op.op !== 'destroy') continue;
        const pending = [row(op.command)];
        while (pending.length) {
            const destroyed = pending.shift()!;
            const inventory = destroyed.inventory_id;
            if (row(destroyed.spill).log) log('inventory_container_spilled', { roll_item_id: inventory, roll_to: inventory });
            if (destroyed.corpse) log('corpse_destroyed', { roll_item_id: inventory });
            log(destroyed.rubble || destroyed.outcome === 'breach' ? 'inventory_destroyed_rubble' : 'inventory_destroyed_vanished', { roll_item_id: inventory, roll_to: inventory });
            pending.push(...rows(row(destroyed.spill).destroyed));
        }
    }
    return result;
}

/** A campaign subscriber needs presentation inputs, never the command's entity graphs. */
export function campaignTruthPresentation(command: Row): Row {
    const keep = ['schema', 'operation_id', 'encounter_id', 'actor_id', 'ability_id', 'weapon_id', 'target_id', 'campaign_id',
        'roll_rows', 'roll_updates', 'saved_rolls', 'roll_private', 'sound', 'log_participants', 'roll_presentations', 'presentation_user_id'];
    return { ...Object.fromEntries(keep.filter(key => key in command).map(key => [key, command[key]])),
        truth_presentation_rows: truthPresentationRows(command),
        verdict: { areaCasts: row(command.verdict).areaCasts, forcedMovements: row(command.verdict).forcedMovements,
            teleportOffers: truthTeleportOffers(command) } };
}
const presented = new Set<string>();

export async function presentTruthCommand(command: Row): Promise<void> {
    if (!window.ftHandleRollInsert) return;
    const operation = String(command.operation_id);
    if (presented.has(operation)) return;
    presented.add(operation);
    for (const saved of rows(command.saved_rolls))
        await window.ftHandleRollInsert({ type: 'roll_insert', scope: 'campaign', roll: saved } as unknown as Parameters<NonNullable<typeof window.ftHandleRollInsert>>[0]);
    if (presented.size > 512) presented.delete(presented.values().next().value!);
    if (Array.isArray(command.roll_rows)) {
        const updates = rows(command.roll_updates);
        if (updates.length) {
            const { handleRollUpdate } = await import('./roll-update');
            for (const roll of updates) await handleRollUpdate({ type: 'roll_update', reason: 'reveal', roll });
        }
        // fetch_rolls returns descending ids; feed the group in insertion order.
        for (const roll of rows(command.roll_rows).sort((a, b) => Number(a.roll_i) - Number(b.roll_i)))
            await window.ftHandleRollInsert({ type: 'roll_insert', scope: 'encounter', roll });
        const groups = new Set(rows(command.roll_rows).map(roll => String(roll.roll_id)));
        for (const id of groups) await window.ftCompleteTruthRollGroup?.(id);
        return;
    }
    const encounterId = String(command.encounter_id ?? ''), encounter = window.FateTide?.encounterCache?.get(encounterId) as Row | undefined;
    const roster = rows(encounter?.encounter_creatures_arr);
    const held = (id: string): Row | undefined => roster.find(member => String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id')) === id)
        ?? (window.FateTide?.objectCache?.get(id) as Row | undefined)
        ?? row(encounter?.catalog)[id] as Row | undefined;
    for (const input of truthPresentationRows(command)) {
        const message = localRollInsert(input, command, encounterId, held);
        if (message) {
            message.roll.roll_created_by_id = command.presentation_user_id ?? window.FateTide?.user?.id;
            await window.ftHandleRollInsert(message);
        }
    }
    if (command.schema === 'janus.action.ability.v2') presentLocalAbilityVfx(command, held);
    await window.ftCompleteTruthRollGroup?.(String(command.operation_id));
}
